/**
 * The wire contract between a phone and the server.
 *
 * ── The one rule ────────────────────────────────────────────────────────────
 * A client may send an AIM RAY AND A TIME. It may never send a hit, a kill, a
 * zone, or a score. Every score-affecting decision is made by the server from
 * its own herd and its own shell ledger (ROUNDTABLE.md, A-3/A-4). If you are
 * ever tempted to add `points` to a client message, that is the moment this
 * game becomes a leaderboard of whoever opened devtools first.
 *
 * ── Time ────────────────────────────────────────────────────────────────────
 * All gameplay timestamps are SERVER time in milliseconds. The client learns
 * the offset from `ping`/`pong` and never asserts its own clock (A-10).
 */

import type { DeerSpawn, HitZone } from './deer.js';
import type { VarmintRecipe, WaveKind } from './sites.js';

export const PROTOCOL_VERSION = 1;

// ── limits ──────────────────────────────────────────────────────────────────

/** Frame cap, enforced by `ws` at the frame layer before anything is buffered. */
export const MAX_MESSAGE_BYTES = 2048;

/**
 * Per-socket message ceiling.
 *
 * This is a DoS guard, NOT a gameplay limit — the shell ledger is the gameplay
 * limit, and it is the one that decides whether a shot counts. So the ceiling
 * has to sit above anything a human hand can produce, or it becomes a hidden
 * rule that punishes fast players.
 *
 * It was 90, and `npm run sim` caught that being wrong: bots tapping through a
 * varmint round tripped RATE_LIMITED four times in a single hunt. A varmint
 * wave gives twelve shells and an 850 ms reload, and someone hammering it with
 * two thumbs can genuinely sustain ~10 taps a second. 160 in ten seconds is
 * comfortably past that and still trivial to serve: at the 2 KB frame cap it
 * is 32 KB/s per socket, and the hard close at 2× catches an actual flood.
 */
export const RATE_LIMIT_MESSAGES = 160;
export const RATE_LIMIT_WINDOW_MS = 10_000;

/**
 * How far back a shot may be timestamped before the server clamps it (A-11).
 *
 * 220 ms, not the 300 the plan started with. `npm run check:herd` measures how
 * far a deer travels during a full rewind, and at 300 ms the fastest animal in
 * the last wave moved 1.96 body lengths — meaning a player on a bad connection
 * was routinely killing deer that everyone ELSE had already watched run past
 * that spot, which reads as the game being wrong rather than the network being
 * slow. At 220 ms the worst case is 1.44 body lengths.
 *
 * The window still covers a 440 ms round trip, which is past the 99th
 * percentile of real mobile latency. Compensation exists to be fair to a
 * laggy player, not to be unfair to everyone standing next to them.
 */
export const MAX_LAG_COMP_MS = 220;

/** Server → client keepalive cadence, and the silence that means a dead path. */
export const SERVER_PING_MS = 20_000;
export const LIVENESS_TIMEOUT_MS = 55_000;
/** Silence from the *page* (not the socket) that means the player has gone. */
export const APP_LIVENESS_MS = 40_000;

/** A saloon with nobody in it, and a saloon nobody is doing anything in. */
export const EMPTY_SALOON_IDLE_MS = 60_000;
export const SALOON_IDLE_MS = 30 * 60_000;

/** Seats per saloon. Eight is the bar rail; more is a crowd nobody can read. */
export const MAX_SEATS = 8;

export const MIN_HANDLE = 1;
export const MAX_HANDLE = 14;

/** Codes are 5 chars from an alphabet with no I/O/0/1 — 11.8M of them. */
export const CODE_LENGTH = 5;
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

// ── identity ────────────────────────────────────────────────────────────────

export type SeatId = string;

export interface SeatView {
  id: SeatId;
  handle: string;
  /** Seat colour index, for the crosshair and the scoreboard chip. */
  hue: number;
  online: boolean;
  isHost: boolean;
  /** Has anted up for this hunt. */
  ready: boolean;
  score: number;
  kills: number;
  /** Biggest rack taken this hunt. */
  bestRack: number;
  shots: number;
  hits: number;
  streak: number;
  /** Benched for the rest of this wave because they shot a doe. */
  benched: boolean;
}

// ── saloon state ────────────────────────────────────────────────────────────

export type Phase = 'bar' | 'countdown' | 'wave' | 'intermission' | 'over';

export interface SaloonView {
  code: string;
  /** Listed in the public saloon list. */
  open: boolean;
  phase: Phase;
  seats: SeatView[];
  hostId: SeatId | null;
  /** 1-based; 0 outside a hunt. */
  waveIndex: number;
  waveKind: WaveKind | null;
  siteId: string | null;
  siteName: string | null;
  /** Server time the current phase ends. 0 if it has no deadline. */
  phaseEndsAt: number;
  /** Set when the whole hunt is done. */
  results: HuntResults | null;
}

export interface HuntResults {
  /** Highest first. */
  standings: Array<{
    seatId: SeatId;
    handle: string;
    hue: number;
    score: number;
    kills: number;
    bestRack: number;
    accuracy: number;
    rank: string;
  }>;
  /** Anything here beat a stored record. */
  records: Array<{ kind: 'score' | 'rack'; handle: string; value: number }>;
}

export interface WaveSummary {
  waveIndex: number;
  siteName: string;
  rows: Array<{ seatId: SeatId; handle: string; hue: number; gained: number; kills: number; does: number }>;
}

// ── leaderboard ─────────────────────────────────────────────────────────────

export interface BoardEntry {
  handle: string;
  score: number;
  bestRack: number;
  /** Server time, ms. */
  at: number;
}

export interface BoardView {
  /** Best full-hunt scores. */
  scores: BoardEntry[];
  /** Biggest racks ever taken here. */
  racks: BoardEntry[];
}

/** One row of the public saloon list. */
export interface SaloonSummary {
  code: string;
  hostHandle: string;
  seats: number;
  maxSeats: number;
  phase: Phase;
  waveIndex: number;
}

// ── client → server ─────────────────────────────────────────────────────────

export type ClientMsg =
  /** Clock sync. `c` is the client's own send time, echoed back untouched. */
  | { t: 'ping'; c: number }
  /** Ask for the public saloon list. */
  | { t: 'list' }
  /** Ask for the leaderboard. */
  | { t: 'board' }
  | { t: 'create'; handle: string; open: boolean }
  | { t: 'join'; code: string; handle: string; token?: string }
  | { t: 'leave' }
  /** Ante up / step back from the rail. */
  | { t: 'ready'; ready: boolean }
  /** Host only. */
  | { t: 'start' }
  /** Host only, from the results card. */
  | { t: 'again' }
  /**
   * A shot. `ax`/`ay` are STAGE coordinates (see stage.ts), `st` is the SERVER
   * time the player believes they fired at, and `seq` lets the client match the
   * outcome to its own optimistic muzzle flash.
   */
  | { t: 'shoot'; ax: number; ay: number; st: number; seq: number }
  | { t: 'reload' };

// ── server → client ─────────────────────────────────────────────────────────

export type ServerMsg =
  | { t: 'hello'; version: number; serverTime: number }
  /** `c` is the client's echoed send time; `s` is server time at reply. */
  | { t: 'pong'; c: number; s: number }
  | { t: 'saloons'; list: SaloonSummary[] }
  | { t: 'board'; board: BoardView }
  | { t: 'joined'; code: string; seatId: SeatId; token: string; view: SaloonView }
  | { t: 'view'; view: SaloonView }
  /**
   * A wave begins. The entire herd is described up front — every client can
   * then draw the whole wave without another position message ever arriving.
   */
  | {
      t: 'wave';
      waveIndex: number;
      kind: WaveKind;
      siteId: string;
      startsAt: number;
      endsAt: number;
      shells: number;
      deer: DeerSpawn[];
      varmints: Array<VarmintRecipe & { id: number; t0: number; t1: number }>;
    }
  /** Somebody fired. Broadcast so other players see the tracer and the flash. */
  | { t: 'shot'; seatId: SeatId; ax: number; ay: number; st: number; hit: boolean }
  | {
      t: 'kill';
      seatId: SeatId;
      deerId: number;
      zone: HitZone;
      rack: number;
      points: number;
      streak: number;
      score: number;
      st: number;
    }
  /** The public shaming. Everyone sees who did it (A-13). */
  | { t: 'doe'; seatId: SeatId; deerId: number; penalty: number; score: number }
  | { t: 'escaped'; deerId: number }
  | { t: 'varmint'; seatId: SeatId; varmintId: number; kind: 'critter' | 'skunk'; points: number; score: number }
  /** Private: your own shell ledger. Never broadcast. */
  | { t: 'shells'; n: number; max: number; reloadUntil: number }
  /**
   * Private: you hit a deer somebody else had already dropped. You are told
   * whose it was, and — deliberately — your streak survives it. Losing a kill
   * to 90 ms of latency should not also cost you a multiplier.
   */
  | { t: 'contested'; deerId: number; byHandle: string }
  /** Your own shot was refused — no shell, mid-reload, or benched. */
  | { t: 'dryfire'; seq: number; reason: 'empty' | 'reloading' | 'benched' | 'closed' }
  | { t: 'summary'; summary: WaveSummary; nextAt: number }
  | { t: 'error'; code: ErrorCode; message: string };

// ── errors ──────────────────────────────────────────────────────────────────

export type ErrorCode =
  | 'BAD_INPUT'
  | 'SALOON_NOT_FOUND'
  | 'SALOON_FULL'
  | 'HUNT_IN_PROGRESS'
  | 'NOT_HOST'
  | 'NOT_READY'
  | 'RATE_LIMITED'
  | 'AT_CAPACITY'
  | 'SERVER_RESTARTING'
  | 'SERVER_ERROR';

export const ERROR_TEXT: Record<ErrorCode, string> = {
  BAD_INPUT: 'That did not make sense.',
  SALOON_NOT_FOUND: 'No saloon by that name.',
  SALOON_FULL: 'That rail is full.',
  HUNT_IN_PROGRESS: 'They are already out hunting. Try the next one.',
  NOT_HOST: 'Only whoever bought the round can start it.',
  NOT_READY: 'Nobody has anted up yet.',
  RATE_LIMITED: 'Slow down.',
  AT_CAPACITY: 'The place is packed. Try again in a minute.',
  SERVER_RESTARTING: 'The saloon is closing for a moment. Back shortly.',
  SERVER_ERROR: 'Something broke back there.',
};

/** WebSocket close codes in the application range. */
export const CLOSE = {
  NEVER_JOINED: 4000,
  TRY_LATER: 4001,
  SERVICE_RESTART: 4002,
} as const;

// ── input hygiene ───────────────────────────────────────────────────────────

/**
 * Handles are rendered into strangers' phones, so they are whitelisted rather
 * than blacklisted (A-6): letters, digits, space and a few separators, nothing
 * else, no combining marks, no direction overrides, no zero-width anything.
 * Rendering is via text nodes only — this is defence in depth, not the defence.
 */
export function cleanHandle(raw: unknown): string {
  const s = String(raw ?? '')
    .normalize('NFKC')
    .replace(/[^A-Za-z0-9 ._-]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_HANDLE);
  return s;
}

export function normalizeCode(raw: unknown): string {
  return String(raw ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, CODE_LENGTH);
}

export function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
